// TasksTodayPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Select, TextInput, Tooltip, Modal } from "flowbite-react";
import { motion } from "framer-motion";
import axios from "../config/axiosInstance";
import { CRM, USERS, INTEGRATIONS } from "../config/apiConfig";
import {
  HiMail,
  HiViewList,
  HiX,
  HiSearch,
  HiUser,
  HiCalendar,
  HiClock,
  HiCheck,
  HiPhone,
  HiVideoCamera,
  HiUserGroup,
  HiChevronLeft,
  HiChevronRight,
  HiFilter,
} from "react-icons/hi";
import { BiTask } from "react-icons/bi";
import { HiCalendarDays, HiOutlineSparkles } from "react-icons/hi2";
import TaskModal from "../components/modals/TaskModal";

/* ===================== Datas helpers ===================== */
const pad2 = (n) => String(n).padStart(2, "0");
const toISODate = (d) => {
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  return dt.toISOString().slice(0, 10);
};
// Constrói Date no fuso local a partir de "YYYY-MM-DD"
function fromISODateLocal(iso) {
  if (!iso) return new Date(NaN);
  if (iso instanceof Date) return new Date(iso);
  const [y, m, d] = String(iso).slice(0, 10).split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}
const toBRDate = (iso) =>
  iso ? String(iso).slice(0, 10).split("-").reverse().join("/") : "—";
const addDays = (iso, days) => {
  const d = fromISODateLocal(iso);
  d.setDate(d.getDate() + days);
  return toISODate(d);
};
const sameDay = (isoA, isoB) =>
  String(isoA).slice(0, 10) === String(isoB).slice(0, 10);

function startOfWeekISO(iso, weekStartsOn = 1 /* Monday */) {
  const d = fromISODateLocal(iso);
  const day = d.getDay(); // 0..6 (Sun..Sat)
  const current = day === 0 ? 7 : day; // 1..7 (Mon..Sun)
  const diff = current - weekStartsOn;
  d.setDate(d.getDate() - diff);
  return toISODate(new Date(d.getFullYear(), d.getMonth(), d.getDate()));
}
function endOfWeekISO(iso, weekStartsOn = 1) {
  const start = fromISODateLocal(startOfWeekISO(iso, weekStartsOn));
  start.setDate(start.getDate() + 6);
  return toISODate(start);
}
function startOfMonthISO(iso) {
  const d = fromISODateLocal(iso);
  return toISODate(new Date(d.getFullYear(), d.getMonth(), 1));
}
function endOfMonthISO(iso) {
  const d = fromISODateLocal(iso);
  return toISODate(new Date(d.getFullYear(), d.getMonth() + 1, 0));
}

/* ============================================================
 * Stage helpers (robustos)
 * ============================================================ */
// Descobre o stageId do card, independente do nome do campo que veio do backend
function getStageIdFromCard(card) {
  if (!card) return null;
  const candidates = [
    card?.stageId,
    card?.stage_id,
    card?.stage?.id,
    card?.stage?.stageId,
    card?.funnelStageId,
    card?.currentStageId,
    card?.stage, // às vezes só vem o id em stage
  ];
  const val = candidates.find((v) => v !== undefined && v !== null && v !== "");
  return val ?? null;
}

// Normaliza lista de etapas pra { id, name, order, color }
function normalizeStages(stages) {
  return (Array.isArray(stages) ? stages : [])
    .map((s, i) => {
      const id = s?.id ?? s?.stageId ?? s?.stage_id ?? s?._id ?? `${i + 1}`;
      const name =
        s?.name ??
        s?.title ??
        String(s?.id ?? s?.stageId ?? s?.stage_id ?? `Etapa ${i + 1}`);
      const orderCandidate = Number.isFinite(+s?.order)
        ? +s.order
        : Number.isFinite(+s?.position)
        ? +s.position
        : Number.isFinite(+s?.index)
        ? +s.index
        : i + 1;
      const color = s?.color || s?.hex || s?.bgColor || "#6b7280"; // gray-500 default
      return { id, name, order: orderCandidate, color };
    })
    .filter((s) => s.id !== undefined && s.id !== null && s.id !== "")
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

/* Month matrix (Mon-Sun) */
function buildMonthMatrix(anchorISO) {
  const startMonth = fromISODateLocal(startOfMonthISO(anchorISO));
  const endMonth = fromISODateLocal(endOfMonthISO(anchorISO));
  const start = fromISODateLocal(startOfWeekISO(startMonth, 1));
  const end = fromISODateLocal(endOfWeekISO(endMonth, 1));

  const weeks = [];
  let cursor = new Date(start);
  while (cursor <= end) {
    const row = [];
    for (let i = 0; i < 7; i++) {
      row.push(toISODate(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    weeks.push(row);
  }
  return weeks;
}

/* ============================================================
 * Filtros persistidos
 * ============================================================ */
const LS_KEY = "tasksToday.filters.v3";
function readFilters() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}
function writeFilters(filters) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(filters));
  } catch {}
}

/* ============================================================
 * Lógica de atraso / campos visuais
 * ============================================================ */
function isOverdueTask(task, refISO) {
  if (!task?.dueDate || !refISO) return false;
  if (task?.status !== "open") return false;
  const dueISO = toISODate(task.dueDate);
  return !!dueISO && dueISO < String(refISO).slice(0, 10);
}

const TYPE_LABEL = {
  ligacao: "Ligação",
  reuniao: "Reunião",
  email: "E-mail",
  visita: "Visita",
  followup: "Follow-up",
};
const TYPE_ICON = {
  ligacao: HiPhone,
  reuniao: HiVideoCamera,
  email: HiMail,
  visita: HiUserGroup,
  followup: HiClock,
};
const TYPE_ACCENT = {
  ligacao: "from-red-500 to-pink-500",
  reuniao: "from-indigo-500 to-violet-500",
  email: "from-sky-500 to-cyan-500",
  visita: "from-emerald-500 to-lime-500",
  followup: "from-amber-500 to-orange-500",
  default: "from-gray-400 to-gray-500",
};
const statusChip = (status) => {
  if (status === "done")
    return "bg-green-50 text-green-700 border border-green-200";
  if (status === "canceled")
    return "bg-rose-50 text-rose-700 border border-rose-200";
  return "bg-blue-50 text-blue-700 border-blue-200";
};
function extractTime(isoStr) {
  if (!isoStr) return { hour: null, minute: null, label: "—", hasTime: false };
  const d = new Date(isoStr);
  const h = d.getHours();
  const m = d.getMinutes();
  if (Number.isNaN(h))
    return { hour: null, minute: null, label: "—", hasTime: false };
  return { hour: h, minute: m, label: `${pad2(h)}:${pad2(m)}`, hasTime: true };
}

/* Agrupa por hora (para modo Dia) */
function groupByHour(tasks) {
  const byHour = new Map();
  const noTime = [];
  let minH = 24,
    maxH = 0,
    hasAnyTime = false;

  tasks.forEach((t) => {
    const tm = extractTime(t?.dueDate);
    if (!tm.hasTime || tm.hour === null) {
      noTime.push(t);
      return;
    }
    hasAnyTime = true;
    minH = Math.min(minH, tm.hour);
    maxH = Math.max(maxH, tm.hour);
    if (!byHour.has(tm.hour)) byHour.set(tm.hour, []);
    byHour.get(tm.hour).push(t);
  });

  if (!hasAnyTime) return { byHour, noTime, hoursWindow: [9, 18] };

  const start = Math.max(6, Math.min(9, minH) - 1);
  const end = Math.min(22, Math.max(18, maxH) + 1);
  return { byHour, noTime, hoursWindow: [start, end] };
}

async function fetchStagesFromFunnel(funnelId) {
  if (!funnelId) return [];
  try {
    const res = await axios.get(`${CRM.FUNNEL_DETAILS}/${funnelId}/details`);
    const data = res?.data?.data || {};
    // Normaliza como no seu fetchFunnel (aqui ignoramos os cards e mantemos id/name/order/color)
    const stages = (data.stages || []).map((st, i) => ({
      id: st?.id,
      name: st?.name ?? `Etapa ${i + 1}`,
      color: st?.color ?? undefined,
      order: Number.isFinite(+st?.order)
        ? +st.order
        : Number.isFinite(+st?.position)
        ? +st.position
        : i + 1,
    }));
    // Ordena por order
    return stages.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  } catch (err) {
    console.error("Erro ao buscar stages do funil:", err);
    return [];
  }
}

/* ============================================================
 * Peças visuais
 * ============================================================ */
function EmptyState({ dateBR, label = "Nada" }) {
  return (
    <div className="text-center py-16 flex flex-col items-center">
      <div className="text-5xl mb-3">
        <BiTask className="text-green-600" />
      </div>
      <h3 className="text-lg font-semibold text-gray-800">
        {label} em {dateBR}
      </h3>
      <p className="text-gray-500">Sem tarefas para o período selecionado.</p>
    </div>
  );
}
const SkeletonRow = () => (
  <div className="grid grid-cols-[88px,1fr] gap-4 items-start">
    <div className="h-6 bg-gray-100 rounded w-full" />
    <div className="h-16 bg-gray-100 rounded" />
  </div>
);
const ResponsibleChip = ({ name, isWeek }) => {
  const assigned = !!(name && String(name).trim());
  if (!assigned) {
    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border-2 border-dashed border-gray-300 ${
          isWeek ? "text-[10px]" : "text-[12px]"
        } text-gray-500`}
      >
        <HiUser className={`${isWeek ? "h-3 w-3" : "h-4 w-4"}`} />
        Não atribuído
      </span>
    );
  }
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-gray-200 bg-gray-50  text-gray-800 ${
        isWeek ? "text-[10px]" : "text-[12px]"
      }`}
    >
      <HiUser className={`${isWeek ? "h-3 w-3" : "h-4 w-4"}`} />
      {name}
    </span>
  );
};
const TypePill = ({ type }) => {
  const TypeIcon = TYPE_ICON[type] || HiClock;
  const typeLabel = TYPE_LABEL[type] || "Ação";
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border bg-white">
      <TypeIcon className="w-3.5 h-3.5 text-gray-600" />
      <span className="text-gray-700">{typeLabel}</span>
    </span>
  );
};
function AvatarInitials({ name = "" }) {
  const parts = String(name).trim().split(/\s+/).slice(0, 2);
  const initials = parts.map((p) => p[0]?.toUpperCase() || "").join("");
  return (
    <div className="h-6 w-6 rounded-full bg-gray-200 text-gray-700 text-[11px] font-semibold flex items-center justify-center">
      {initials || "?"}
    </div>
  );
}
function IconActionButton({ title, onClick, children, color = "light" }) {
  return (
    <Button color={color} size="xs" onClick={onClick} className="!p-1.5">
      {children}
    </Button>
  );
}
function StatusPill({ status }) {
  const map = {
    open: "bg-blue-50 text-blue-700 border-blue-200",
    done: "bg-green-50 text-green-700 border-green-200",
    canceled: "bg-rose-50 text-rose-700 border-rose-200",
  };
  const label =
    status === "done"
      ? "Concluída"
      : status === "canceled"
      ? "Cancelada"
      : "Aberta";
  return (
    <span
      className={`text-[10px] px-2 py-0.5 rounded-full border ${
        map[status] || "bg-gray-50 text-gray-700 border-gray-200"
      }`}
    >
      {label}
    </span>
  );
}

/* Card de tarefa */
function CalendarTaskCard({
  task,
  overdue,
  onDone,
  onCancel,
  onOpenCard,
  compact = false,
  isWeek = false,
}) {
  const type = task?.type || "default";
  const tm = extractTime(task?.dueDate);
  const dueStr = toBRDate(task?.dueDate);
  const statusLabel =
    task?.status === "done"
      ? "Concluída"
      : task?.status === "canceled"
      ? "Cancelada"
      : "Aberta";
  const taskResponsibleName =
    task?.responsible?.name || task?.card?.responsible?.name || "";
  const companyName =
    task?.card?.lead?.name || task?.card?.lead?.companyName || "";
  const summaryLabel = task?.summary || "";
  const displayName = companyName || summaryLabel || "Evento Google";
  const isGoogleEvent = Boolean(task?.source === "google");

  if (compact) {
    // Versão compacta (sem footer de ações) – ideal para Semana/Mês e mobile
    return (
      <div
        className={[
          "rounded-xl border bg-white shadow-sm hover:shadow-md transition-shadow p-2",
          overdue ? "border-rose-300 ring-1 ring-rose-200" : "border-gray-200",
        ].join(" ")}
      >
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2 min-w-0">
            {!isWeek && (
              <div
                className={`w-1.5 h-4 rounded bg-gradient-to-b ${
                  TYPE_ACCENT[type] || TYPE_ACCENT.default
                }`}
              />
            )}

            <TypePill type={type} />
            {!isWeek && (
              <span className="text-[11px] text-gray-500 tabular-nums">
                {tm.label}
              </span>
            )}
          </div>
          <span
            className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${statusChip(
              task?.status
            )}`}
          >
            {statusLabel}
          </span>
        </div>
        <div
          className={`font-semibold text-gray-700 cursor-pointer truncate ${
            isWeek ? "text-[10px]" : "text-[13px]"
          }`}
          onClick={() => !isGoogleEvent && onOpenCard?.(task)}
          title={isGoogleEvent ? undefined : "Ver card"}
        >
          {companyName}
        </div>
        {!isWeek && (
          <div className="mt-1 flex items-center gap-2 text-xs text-gray-700">
            <HiCalendar className="w-4 h-4" />
            <b className={overdue ? "text-rose-700" : ""}>{dueStr}</b>
          </div>
        )}

        {!isGoogleEvent && (
          <div className="mt-1">
            <ResponsibleChip name={taskResponsibleName} isWeek={isWeek} />
          </div>
        )}
      </div>
    );
  }

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.24 }}
      className={[
        "rounded-2xl border bg-white shadow-sm hover:shadow-[0_10px_28px_rgba(16,24,40,0.10)] transition-shadow",
        overdue ? "border-rose-300 ring-1 ring-rose-200" : "border-gray-200",
      ].join(" ")}
    >
      <div className="flex items-center justify-between px-3 py-2.5 border-b bg-gradient-to-r from-white to-gray-50/50">
        <div className="flex items-center gap-2">
          {overdue && (
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200">
              Atrasada
            </span>
          )}
          <div
            className={`w-1.5 h-6 rounded bg-gradient-to-b ${
              TYPE_ACCENT[type] || TYPE_ACCENT.default
            }`}
          />
          <TypePill type={type} />
        </div>
        <span
          className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${statusChip(
            task?.status
          )}`}
        >
          {statusLabel}
        </span>
      </div>

      <div className="px-3 py-3 space-y-2 text-sm">
        <div className="flex items-center gap-2">
          <div className="font-semibold text-gray-600">{displayName}</div>
          {isGoogleEvent && (
            <span className="text-[10px] px-2 py-0.5 rounded-full border border-blue-100 bg-blue-50 text-blue-700">
              Google Calendar
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 text-gray-700">
          <HiCalendar className="w-4 h-4" />
          <b className={overdue ? "text-rose-700" : ""}>{dueStr}</b>
          <span className="text-gray-400">•</span>
          <span
            className={["tabular-nums", overdue ? "text-rose-700" : ""].join(
              " "
            )}
          >
            {tm.label}
          </span>
        </div>

        {!isGoogleEvent && task?.card?.customer?.name && (
          <div className="flex items-center gap-2 text-gray-700">
            <HiUser className="w-4 h-4" />
            <span className="truncate">{task.card.customer.name}</span>
          </div>
        )}

        {!isGoogleEvent && <ResponsibleChip name={taskResponsibleName} />}
      </div>

      {!isGoogleEvent && (
        <div className="px-3 pb-3 pt-1 flex flex-wrap gap-2">
          <Button size="xs" color="light" onClick={() => onOpenCard?.(task)}>
            Ver card
          </Button>
          {task.status === "open" && (
            <>
              <Button size="xs" color="success" onClick={() => onDone(task)}>
                <div className="flex gap-1 items-center">
                  <HiCheck className="mr-1" />
                  <div> Concluir</div>
                </div>
              </Button>
              <Button size="xs" color="failure" onClick={() => onCancel(task)}>
                <div className="flex gap-1 items-center">
                  <HiX className="mr-1" /> <span>Cancelar</span>
                </div>
              </Button>
            </>
          )}
        </div>
      )}
    </motion.div>
  );
}

/* Linha por hora (modo Dia) */
function HourRow({
  hour,
  tasks,
  onDone,
  onCancel,
  onOpenCard,
  isCurrent,
  viewDateISO,
}) {
  return (
    <div className="grid grid-cols-[72px,1fr] sm:grid-cols-[88px,1fr] gap-3 sm:gap-4 py-3 relative">
      <div className="sticky left-0 -ml-2 sm:-ml-3 pl-2 sm:pl-3 pr-1 sm:pr-2 bg-white">
        <div
          className={`inline-flex items-center gap-2 rounded-full px-2 py-0.5 sm:px-2.5 sm:py-1 border text-[11px] sm:text-xs tabular-nums ${
            isCurrent
              ? "bg-gray-50 border-gray-200 text-gray-700"
              : "bg-gray-100 border-gray-200 text-gray-500"
          }`}
        >
          <span
            className={`w-1.5 h-1.5 rounded-full ${
              isCurrent ? "bg-green-500" : "bg-green-300"
            }`}
          />
          {pad2(hour)}:00
        </div>
      </div>

      <div className="relative">
        <div className="absolute inset-x-0 top-0 h-px bg-gray-100" />
        <div className="relative grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2 sm:gap-3">
          {(tasks || []).length === 0 ? (
            <div className="text-xs text-gray-300 italic">—</div>
          ) : (
            tasks.map((t) => (
              <CalendarTaskCard
                key={t.id}
                task={t}
                onDone={onDone}
                onCancel={onCancel}
                onOpenCard={onOpenCard}
                overdue={isOverdueTask(t, viewDateISO)}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

/* Linha da tabela (modo Lista) */
function ListRowTr({ task, overdue, onDone, onCancel, onOpenCard }) {
  const tm = extractTime(task?.dueDate);
  const TypeIcon = TYPE_ICON[task?.type] || HiClock;
  const typeLabel = TYPE_LABEL[task?.type] || "Ação";
  const customer =
    task?.card?.lead?.name || task?.card?.lead?.companyName || "—";
  const resp = task?.responsible?.name || task?.card?.responsible?.name || "";
  const isGoogleEvent = task?.source === "google";

  return (
    <tr className="odd:bg-white even:bg-gray-50 hover:bg-gray-100/60">
      <td className="px-3 py-2 align-middle whitespace-nowrap">
        <div className="flex gap-1 items-center">
          <span className="text-[11px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 tabular-nums mr-1.5">
            {toBRDate(task?.dueDate)}
          </span>
          <span className="text-[11px] px-1.5 py-0.5 rounded bg-white border border-gray-200 text-gray-700 tabular-nums">
            {tm.label}
          </span>
        </div>
      </td>
      <td className="px-3 py-2 align-middle">
        <div className="flex items-center gap-2 min-w-0">
          <span className="inline-flex items-center justify-center h-6 w-6 rounded-md border border-gray-200 bg-white shrink-0">
            <TypeIcon className="w-3.5 h-3.5 text-gray-700" />
          </span>
          <span className="text-sm text-gray-900 font-medium truncate">
            {typeLabel}
          </span>
          {overdue && task?.status === "open" && (
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200">
              Atrasada
            </span>
          )}
          <div>
            <StatusPill status={task?.status} />
          </div>
        </div>
      </td>
      <td className="px-3 py-2 align-middle">
        <div className="flex items-center gap-2">
          <div className="text-sm text-gray-800 truncate" title={customer}>
            {customer}
          </div>
          {isGoogleEvent && (
            <span className="text-[10px] px-2 py-0.5 rounded-full border border-blue-100 bg-blue-50 text-blue-700">
              Google
            </span>
          )}
        </div>
      </td>
      <td className="px-3 py-2 align-middle">
        {!isGoogleEvent ? (
          <div className="flex items-center gap-2 min-w-0">
            <AvatarInitials name={resp} />
            <span
              className="text-sm text-gray-800 truncate"
              title={resp || "Não atribuído"}
            >
              {resp || "Não atribuído"}
            </span>
          </div>
        ) : (
          <span className="text-xs uppercase text-gray-400">Google</span>
        )}
      </td>
      <td className="px-3 py-2 align-middle text-center">
        {!isGoogleEvent && (
          <div className="inline-flex items-center gap-1.5">
            <IconActionButton
              title="Ver card"
              onClick={() => onOpenCard?.(task)}
            >
              <HiViewList className="w-4 h-4" />
            </IconActionButton>
            {task.status === "open" ? (
              <>
                <IconActionButton
                  title="Concluir"
                  color="success"
                  onClick={() => onDone(task)}
                >
                  <HiCheck className="w-4 h-4" />
                </IconActionButton>
                <IconActionButton
                  title="Cancelar"
                  color="failure"
                  onClick={() => onCancel(task)}
                >
                  <HiX className="w-4 h-4" />
                </IconActionButton>
              </>
            ) : null}
          </div>
        )}
      </td>
    </tr>
  );
}

/* ============================================================
 * Página
 * ============================================================ */
export default function TasksTodayPage() {
  const [selectedTask, setSelectedTask] = useState(null);
  const [modalHistory, setModalHistory] = useState([]);
  const [modalStages, setModalStages] = useState([]);

  const [loading, setLoading] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [integrationTokens, setIntegrationTokens] = useState([]);
  const [googleEvents, setGoogleEvents] = useState([]);
  const [loadingGoogleEvents, setLoadingGoogleEvents] = useState(false);
  const [googleError, setGoogleError] = useState("");
  const hasGoogleCalendar = useMemo(
    () =>
      integrationTokens.some(
        (token) =>
          String(token?.tokenType || "").toLowerCase() === "google_calendar"
      ),
    [integrationTokens]
  );

  const [type, setType] = useState(() => readFilters().type ?? "");
  const [responsibleId, setResponsibleId] = useState(
    () => readFilters().responsibleId ?? ""
  );
  const [q, setQ] = useState(() => readFilters().q ?? "");
  const [status, setStatus] = useState(() => readFilters().status ?? "open");
  const [filterModalOpen, setFilterModalOpen] = useState(false);

  // viewMode: "calendar" | "list"
  const [viewMode, setViewMode] = useState(
    () => readFilters().viewMode ?? "calendar"
  );
  // calendarMode: "day" | "week" | "month"
  const [calendarMode, setCalendarMode] = useState(
    () => readFilters().calendarMode ?? "day"
  );

  const defaultDate = readFilters().viewDate ?? toISODate(new Date());
  const [viewDateISO, setViewDateISO] = useState(defaultDate);

  // Modal do card
  const [cardModalOpen, setCardModalOpen] = useState(false);
  const [modalCardId, setModalCardId] = useState(null);
  const [modalTaskId, setModalTaskId] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalLabels, setModalLabels] = useState([]);

  const todayISO = useMemo(() => toISODate(new Date()), []);
  const isToday = viewDateISO === todayISO;
  const viewBR = useMemo(() => toBRDate(viewDateISO), [viewDateISO]);

  useEffect(() => {
    writeFilters({
      status,
      type,
      responsibleId,
      q,
      viewMode,
      calendarMode,
      viewDate: viewDateISO,
    });
  }, [status, type, responsibleId, q, viewMode, calendarMode, viewDateISO]);

  // refs para rolar até a hora atual (modo Dia)
  const listRef = useRef(null);
  const now = new Date();
  const nowHour = now.getHours();

  const loadUsers = async () => {
    try {
      const res = await axios.get(`${USERS.GET_USERS}?itemsPerPage=-1`);
      setUsers(res?.data?.data?.data || []);
    } catch (e) {
      console.error("Erro ao buscar usuários", e);
    }
  };

  const loadIntegrationTokens = async () => {
    try {
      const res = await axios.get(INTEGRATIONS.LIST_TOKENS);
      setIntegrationTokens(res?.data?.data || []);
    } catch (error) {
      console.error("Erro ao listar integrações", error);
    }
  };

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const base = { status, q, type, responsibleId };
      let params = { ...base };

      if (calendarMode === "day") {
        params.date = viewDateISO;
      } else if (calendarMode === "week") {
        params.rangeStart = startOfWeekISO(viewDateISO);
        params.rangeEnd = endOfWeekISO(viewDateISO);
      } else if (calendarMode === "month") {
        params.rangeStart = startOfMonthISO(viewDateISO);
        params.rangeEnd = endOfMonthISO(viewDateISO);
      }

      // remove vazios
      Object.keys(params).forEach((k) => {
        if (!params[k]) delete params[k];
      });

      const res = await axios.get(`${CRM.TODAY_TASKS}`, { params });
      setTasks(res?.data?.data || []);
    } catch (e) {
      console.error("Erro ao listar tarefas", e);
    } finally {
      setLoading(false);
    }
  };

  const buildViewRange = () => {
    if (!viewDateISO) return { start: null, end: null };
    let start = null;
    let end = null;
    if (calendarMode === "day") {
      start = fromISODateLocal(viewDateISO);
      start.setHours(0, 0, 0, 0);
      end = new Date(start);
      end.setHours(23, 59, 59, 999);
    } else if (calendarMode === "week") {
      start = fromISODateLocal(startOfWeekISO(viewDateISO));
      start.setHours(0, 0, 0, 0);
      end = fromISODateLocal(endOfWeekISO(viewDateISO));
      end.setHours(23, 59, 59, 999);
    } else {
      start = fromISODateLocal(startOfMonthISO(viewDateISO));
      start.setHours(0, 0, 0, 0);
      end = fromISODateLocal(endOfMonthISO(viewDateISO));
      end.setHours(23, 59, 59, 999);
    }
    return { start, end };
  };

  const fetchGoogleEvents = async () => {
    if (!hasGoogleCalendar) {
      setGoogleEvents([]);
      setGoogleError("");
      return;
    }
    const { start, end } = buildViewRange();
    if (!start || !end) {
      setGoogleEvents([]);
      setGoogleError("");
      return;
    }
    setLoadingGoogleEvents(true);
    setGoogleError("");
    try {
      const params = {
        timeMin: start.toISOString(),
        timeMax: end.toISOString(),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        maxResults: 200,
      };
      const res = await axios.get(INTEGRATIONS.GOOGLE_CALENDAR_EVENTS, {
        params,
      });
      setGoogleEvents(res?.data?.data || []);
    } catch (e) {
      console.error("Erro ao buscar eventos do Google Calendar", e);
      setGoogleEvents([]);
      setGoogleError(
        e?.response?.data?.message || "Não foi possível buscar eventos."
      );
    } finally {
      setLoadingGoogleEvents(false);
    }
  };

  // 2) Substitua seu fetchCardDetails por este
  const fetchCardDetails = async (cardId) => {
    if (!cardId) return null;
    try {
      setModalLoading(true);

      const [cardRes, histRes] = await Promise.all([
        axios.get(`${CRM.GET_CARD}/${cardId}/details`),
        axios.get(`${CRM.GET_CARD_HISTORY}/${cardId}/history`),
      ]);

      const fullRaw = cardRes?.data?.data || null;
      const history = histRes?.data?.data || [];

      // Descobre o funnelId do card (vários fallbacks)
      const funnelId = fullRaw?.stage?.funnelId;

      // Busca os stages via FUNIL (como no seu fetchFunnel)
      const stagesFromCard = await fetchStagesFromFunnel(funnelId);

      // Garante stageId dentro do objeto do card
      const detectedStageId =
        fullRaw?.stageId ??
        fullRaw?.stage_id ??
        fullRaw?.stage?.id ??
        fullRaw?.stage?.stageId ??
        fullRaw?.funnelStageId ??
        fullRaw?.currentStageId ??
        null;

      const fullCard = {
        ...fullRaw,
        stageId:
          detectedStageId ??
          (stagesFromCard.length ? stagesFromCard[0].id : null),
      };

      // (opcional) tags/labels
      const labelsFromCard =
        fullRaw?.availableTags ||
        fullRaw?.labels ||
        fullRaw?.tags ||
        fullRaw?.availableLabels ||
        [];

      return { fullCard, history, stagesFromCard, labelsFromCard };
    } catch (e) {
      console.error("Erro ao carregar detalhes do card", e);
      return null;
    } finally {
      setModalLoading(false);
    }
  };

  const openCardFromTask = async (task) => {
    const cardId = task?.card?.id;
    if (!cardId) return;

    setModalCardId(cardId);
    setModalTaskId(task?.id || null);
    setCardModalOpen(true); // já abre; conteúdo aparece quando carregar

    const loaded = await fetchCardDetails(cardId);
    if (!loaded) return;

    const { fullCard, history, stagesFromCard, labelsFromCard } = loaded;

    // alimenta o modal com dados completos
    setSelectedTask(fullCard);
    setModalHistory(history);
    setModalStages(stagesFromCard);
    setModalLabels(labelsFromCard);
  };

  const closeCardModal = () => {
    setCardModalOpen(false);
    setSelectedTask(null);
    setModalHistory([]);
    setModalCardId(null);
    setModalTaskId(null);
    fetchTasks();
  };

  useEffect(() => {
    loadUsers();
    loadIntegrationTokens();
  }, []);

  useEffect(() => {
    fetchTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, type, responsibleId, viewDateISO, calendarMode]);

  useEffect(() => {
    fetchGoogleEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasGoogleCalendar, viewDateISO, calendarMode]);

  // rola para a hora atual quando carregar (só calendário-dia e se a data for hoje)
  useEffect(() => {
    if (viewMode !== "calendar" || calendarMode !== "day" || !isToday) return;
    const el = listRef.current;
    if (!el) return;
    const currentRow = el.querySelector(`[data-hour="${nowHour}"]`);
    if (currentRow) {
      const offset = currentRow.offsetTop - 80;
      el.scrollTo({ top: offset, behavior: "smooth" });
    }
  }, [loading, viewMode, calendarMode, isToday]); // após carregar/trocar modo

  const handleSearch = (e) => {
    e?.preventDefault?.();
    fetchTasks();
  };

  // Navegação dependente do modo (dia/semana/mês)
  const setToday = () => setViewDateISO(todayISO);
  const shiftByMode = (delta) => {
    if (calendarMode === "day") {
      setViewDateISO(addDays(viewDateISO, delta));
    } else if (calendarMode === "week") {
      setViewDateISO(addDays(viewDateISO, delta * 7));
    } else {
      // mês
      const d = fromISODateLocal(viewDateISO);
      d.setMonth(d.getMonth() + delta);
      setViewDateISO(toISODate(d));
    }
  };

  // ações
  const markDone = async (t) => {
    try {
      await axios.post(`${CRM.COMPLETE_TASK}/${t.id}/complete`);
    } catch (e) {
      console.error("Erro ao concluir", e);
    } finally {
      setTasks((prev) =>
        prev.map((i) =>
          i.id === t.id
            ? { ...i, status: "done", completedAt: new Date().toISOString() }
            : i
        )
      );
    }
  };
  const cancelTask = async (t) => {
    try {
      await axios.post(`${CRM.CANCEL_TASK}/${t.id}/cancel`);
      setTasks((prev) =>
        prev.map((i) => (i.id === t.id ? { ...i, status: "canceled" } : i))
      );
    } catch (e) {
      console.error("Erro ao cancelar", e);
    }
  };
  const updateTask = async (taskId, patch) => {
    try {
      // await axios.patch(`${CRM.TASKS}/${taskId}`, patch);
      setTasks((prev) =>
        prev.map((t) => (t.id === taskId ? { ...t, ...patch } : t))
      );
    } catch (e) {
      console.error("Erro ao atualizar tarefa", e);
    }
  };

  // aceita (cardId, targetStageId) OU ({ cardId, toStageId })
  const onMoveStageFromModal = async (...args) => {
    try {
      let cardId = null;
      let targetStageId = null;

      if (args.length === 2) {
        [cardId, targetStageId] = args;
      } else if (args[0] && typeof args[0] === "object") {
        const payload = args[0];
        cardId = payload.cardId ?? payload.id ?? payload.card?.id ?? null;
        targetStageId =
          payload.toStageId ?? payload.stageId ?? payload.targetStageId ?? null;
      }

      if (!cardId || !targetStageId) return;

      // Se existir endpoint real pra mover:
      // await axios.post(`${CRM.CARDS}/${cardId}/move`, { stageId: targetStageId });

      // Após mover, atualize modal + lista
      await onRefetchCard();
      await fetchTasks();
    } catch (e) {
      console.error("Erro ao mover card", e);
    }
  };

  const onRefetchCard = async () => {
    if (!modalCardId) return;
    const loaded = await fetchCardDetails(modalCardId);
    if (!loaded) return;
    const { fullCard, history, stagesFromCard, labelsFromCard } = loaded;
    setSelectedTask(fullCard);
    setModalHistory(history);
    setModalStages(stagesFromCard);
    setModalLabels(labelsFromCard);
    // também é legal atualizar a lista/contadores
    fetchTasks();
  };

  const onCardDeleted = (deletedCardId) => {
    setTasks((prev) => prev.filter((t) => t?.card?.id !== deletedCardId));
    closeCardModal();
  };

  // Filtros de status para contadores/visões
  const normalizedGoogleEvents = useMemo(() => {
    return googleEvents
      .map((event) => {
        const start = event?.start?.dateTime || event?.start?.date;
        const dueDate = start || viewDateISO;
        if (!dueDate) return null;
        const summary = event?.summary || "Evento Google";
        return {
          id: `google-${event?.id || Math.random()}`,
          dueDate,
          type: "reuniao",
          status: event?.status === "cancelled" ? "canceled" : "open",
          card: {
            lead: {
              name: summary,
            },
          },
          summary,
          source: "google",
          responsible: {
            name:
              event?.organizer?.displayName ||
              event?.creator?.displayName ||
              "",
          },
          rawEvent: event,
        };
      })
      .filter(Boolean);
  }, [googleEvents, viewDateISO]);

  const combinedTasks = useMemo(
    () => [...tasks, ...normalizedGoogleEvents],
    [tasks, normalizedGoogleEvents]
  );

  const filtered = useMemo(() => {
    const base =
      status === "all"
        ? combinedTasks
        : combinedTasks.filter((t) => t.status === status);
    if (type) {
      return base.filter((t) => (t.type || "").toLowerCase() === type);
    }
    return base;
  }, [status, type, combinedTasks]);

  const openCount = tasks.filter((t) => t.status === "open").length;
  const doneCount = tasks.filter((t) => t.status === "done").length;
  const canceledCount = tasks.filter((t) => t.status === "canceled").length;

  /* ====== Preparos por modo ====== */

  // DIA
  const { byHour, noTime, hoursWindow } = useMemo(
    () => groupByHour(filtered.filter((t) => sameDay(t?.dueDate, viewDateISO))),
    [filtered, viewDateISO]
  );
  const hours = useMemo(() => {
    const [start, end] = hoursWindow;
    const list = [];
    for (let h = start; h <= end; h++) list.push(h);
    return list;
  }, [hoursWindow]);
  const showNow =
    viewMode === "calendar" &&
    calendarMode === "day" &&
    isToday &&
    now.getHours() >= hours[0] &&
    now.getHours() <= hours[hours.length - 1];

  // SEMANA
  const weekStartISO = startOfWeekISO(viewDateISO);
  const weekEndISO = endOfWeekISO(viewDateISO);
  const weekDays = useMemo(() => {
    const arr = [];
    let c = fromISODateLocal(weekStartISO);
    for (let i = 0; i < 7; i++) {
      arr.push(toISODate(c));
      c.setDate(c.getDate() + 1);
    }
    return arr;
  }, [weekStartISO]);
  const tasksByDayWeek = useMemo(() => {
    const map = new Map();
    weekDays.forEach((d) => map.set(d, []));
    filtered.forEach((t) => {
      const d = toISODate(t?.dueDate || t?.createdAt || new Date());
      if (d >= weekStartISO && d <= weekEndISO) {
        if (!map.has(d)) map.set(d, []);
        map.get(d).push(t);
      }
    });
    // ordenar por hora
    weekDays.forEach((d) =>
      map.get(d)?.sort((a, b) => {
        const ta = +new Date(a?.dueDate || 0);
        const tb = +new Date(b?.dueDate || 0);
        return ta - tb;
      })
    );
    return map;
  }, [filtered, weekDays, weekStartISO, weekEndISO]);

  // MÊS
  const monthMatrix = useMemo(
    () => buildMonthMatrix(viewDateISO),
    [viewDateISO]
  );
  const firstMonthDay = startOfMonthISO(viewDateISO);
  const lastMonthDay = endOfMonthISO(viewDateISO);
  const tasksByDayMonth = useMemo(() => {
    const map = new Map();
    monthMatrix.flat().forEach((d) => map.set(d, []));
    filtered.forEach((t) => {
      const d = toISODate(t?.dueDate || t?.createdAt || new Date());
      if (d >= monthMatrix[0][0] && d <= monthMatrix.at(-1)?.at(-1)) {
        if (!map.has(d)) map.set(d, []);
        map.get(d).push(t);
      }
    });
    monthMatrix.flat().forEach((d) =>
      map.get(d)?.sort((a, b) => {
        const ta = +new Date(a?.dueDate || 0);
        const tb = +new Date(b?.dueDate || 0);
        return ta - tb;
      })
    );
    return map;
  }, [filtered, monthMatrix]);

  /* ====== Render ====== */
  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="hidden md:flex flex-col gap-4">
        {/* Hero */}
        <div className="rounded-2xl overflow-hidden border border-gray-200 shadow-sm">
          <div
            className="p-4 sm:p-6 md:p-7"
            style={{
              background:
                "linear-gradient(90deg, #e45412 0%,rgb(201, 103, 58) 100%)",
            }}
          >
            <div className="flex items-center justify-between flex-wrap gap-3 sm:gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-white/90 text-sm">
                  <HiOutlineSparkles className="opacity-90" />
                  <span className="truncate">
                    Sua agenda{" "}
                    {viewMode === "calendar"
                      ? calendarMode === "day"
                        ? "— Dia"
                        : calendarMode === "week"
                        ? "— Semana"
                        : "— Mês"
                      : "— Lista"}
                  </span>
                </div>
                <h1 className="text-white text-xl sm:text-2xl md:text-3xl font-bold tracking-tight">
                  {viewBR}
                </h1>
              </div>

              {/* Navegação de período / modos */}
              <div className="flex items-stretch gap-2 flex-wrap justify-end">
                {!isToday && (
                  <Button
                    size="sm"
                    color="light"
                    onClick={setToday}
                    className="!bg-white/90 !text-[#7A082D] hover:!bg-white"
                  >
                    Hoje
                  </Button>
                )}

                <div className="flex items-center rounded-lg overflow-hidden border border-white/30 bg-white/10">
                  <Tooltip content="Anterior" style="light">
                    <button
                      type="button"
                      onClick={() => shiftByMode(-1)}
                      className="px-2 py-2 text-white/90 hover:bg-white/10"
                      title="Anterior"
                    >
                      <HiChevronLeft className="w-5 h-5" />
                    </button>
                  </Tooltip>

                  <div className="hidden xs:flex items-center gap-2 px-2">
                    <HiCalendar className="text-white" />
                    <input
                      type="date"
                      className="bg-transparent text-white placeholder-white text-sm outline-none border-0 focus:ring-0 [color-scheme:dark]"
                      value={viewDateISO}
                      onChange={(e) => setViewDateISO(e.target.value)}
                      aria-label="Selecionar data"
                    />
                  </div>

                  <Tooltip content="Próximo" style="light">
                    <button
                      type="button"
                      onClick={() => shiftByMode(1)}
                      className="px-2 py-2 text-white/90 hover:bg-white/10"
                      title="Próximo"
                    >
                      <HiChevronRight className="w-5 h-5" />
                    </button>
                  </Tooltip>
                </div>

                {/* Toggle de visualização */}
                <div className="inline-flex rounded-lg overflow-hidden border border-white/30 bg-white/10">
                  <button
                    type="button"
                    onClick={() => setViewMode("calendar")}
                    className={[
                      "px-3 py-1.5 text-sm flex items-center gap-1",
                      viewMode === "calendar"
                        ? "bg-white text-[#7A082D]"
                        : "text-white/90 hover:bg-white/10",
                    ].join(" ")}
                    title="Visualização em calendário"
                  >
                    <HiCalendarDays />{" "}
                    <span className="hidden sm:inline">Calendário</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode("list")}
                    className={[
                      "px-3 py-1.5 text-sm flex items-center gap-1",
                      viewMode === "list"
                        ? "bg-white text-[#7A082D]"
                        : "text-white/90 hover:bg-white/10",
                    ].join(" ")}
                    title="Visualização em lista"
                  >
                    <HiViewList />{" "}
                    <span className="hidden sm:inline">Lista</span>
                  </button>
                </div>

                {/* Subtoggle do calendário: Dia / Semana / Mês */}
                {viewMode === "calendar" && (
                  <div className="inline-flex rounded-lg overflow-hidden border border-white/30 bg-white/10">
                    {[
                      { key: "day", label: "Dia" },
                      { key: "week", label: "Semana" },
                      { key: "month", label: "Mês" },
                    ].map((opt) => (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => setCalendarMode(opt.key)}
                        className={[
                          "px-3 py-1.5 text-sm",
                          calendarMode === opt.key
                            ? "bg-white text-[#7A082D]"
                            : "text-white/90 hover:bg-white/10",
                        ].join(" ")}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Contadores no hero */}
            <div className="mt-3 sm:mt-4 flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-2 text-xs sm:text-sm bg-white/10 text-white border border-white/20 px-2.5 py-1.5 rounded-full">
                <span className="w-2 h-2 rounded-full bg-blue-200" /> Abertas:{" "}
                <b>{openCount}</b>
              </span>
              <span className="inline-flex items-center gap-2 text-xs sm:text-sm bg-white/10 text-white border border-white/20 px-2.5 py-1.5 rounded-full">
                <span className="w-2 h-2 rounded-full bg-green-200" />{" "}
                Concluídas: <b>{doneCount}</b>
              </span>
              <span className="inline-flex items-center gap-2 text-xs sm:text-sm bg-white/10 text-white border border-white/20 px-2.5 py-1.5 rounded-full">
                <span className="w-2 h-2 rounded-full bg-red-200" /> Canceladas:{" "}
                <b>{canceledCount}</b>
              </span>
            </div>
          </div>
        </div>

        {/* Barra de filtros */}
        <div className="bg-white p-3 sm:p-4 md:p-5 rounded-2xl border border-gray-200">
          <form onSubmit={handleSearch} className="flex gap-3 sm:gap-4">
            <div className="flex-0">
              <label className="block text-xs text-gray-500 mb-1">Status</label>
              <Select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="all">Todas</option>
                <option value="open">Abertos</option>
                <option value="done">Finalizado</option>
                <option value="canceled">Cancelado</option>
              </Select>
            </div>
            <div className="flex-0">
              <label className="block text-xs text-gray-500 mb-1">Tipo</label>
              <Select value={type} onChange={(e) => setType(e.target.value)}>
                <option value="">Todos</option>
                <option value="ligacao">Ligação</option>
                <option value="reuniao">Reunião</option>
                <option value="email">E-mail</option>
                <option value="visita">Visita</option>
                <option value="followup">Follow-up</option>
              </Select>
            </div>
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-1">
                Responsável
              </label>
              <Select
                value={responsibleId}
                onChange={(e) => setResponsibleId(e.target.value)}
              >
                <option value="">Todos</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-1">Buscar</label>
              <div className="flex gap-2">
                <TextInput
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Cliente, nota..."
                  className="flex-1"
                  icon={HiSearch}
                />
                <Button type="submit" className="shrink-0">
                  Buscar
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>

      <div className="flex flex-col gap-3 md:hidden">
        <div className="rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div
            className="px-4 py-5"
            style={{
              background:
                "linear-gradient(90deg, #e45412 0%,rgb(201, 103, 58) 100%)",
            }}
          >
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[11px] uppercase tracking-[0.4em] text-white/80">
                  Agenda
                </p>
                <p className="text-white font-semibold text-lg">{viewBR}</p>
                <p className="text-xs text-white/70">
                  {viewMode === "calendar"
                    ? calendarMode === "day"
                      ? "Dia"
                      : calendarMode === "week"
                      ? "Semana"
                      : "Mês"
                    : "Lista"}
                </p>
              </div>
              <div className="text-right text-xs text-white/80 space-y-1">
                <div className="text-white font-bold text-base">
                  {openCount} <span className="text-white/70">abertas</span>
                </div>
                <div className="text-white font-bold text-base">
                  {doneCount} <span className="text-white/70">concluídas</span>
                </div>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-2 justify-center">
              <button
                type="button"
                onClick={() => shiftByMode(-1)}
                className="text-[13px] uppercase tracking-[0.4em] text-white/80 rounded-full border border-white/30 px-3 py-1"
              >
                <HiChevronLeft />
              </button>
              <span className="text-white/90 text-sm font-semibold">
                {toBRDate(viewDateISO)}
              </span>
              <button
                type="button"
                onClick={() => shiftByMode(1)}
                className=" text-[13px] uppercase tracking-[0.4em] text-white/80 rounded-full border border-white/30 px-3 py-1"
              >
                <HiChevronRight />
              </button>
            </div>
          </div>
          <div className="bg-white px-4 py-3 border-t border-white/30">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setViewMode("calendar")}
                className={`flex-1 text-xs font-semibold rounded-full px-3 py-2 border ${
                  viewMode === "calendar"
                    ? "border-[#7A082D] text-[#7A082D]"
                    : "border-gray-200 text-gray-600"
                }`}
              >
                Calendário
              </button>
              <button
                type="button"
                onClick={() => setViewMode("list")}
                className={`flex-1 text-xs font-semibold rounded-full px-3 py-2 border ${
                  viewMode === "list"
                    ? "border-[#7A082D] text-[#7A082D]"
                    : "border-gray-200 text-gray-600"
                }`}
              >
                Lista
              </button>
            </div>
          </div>
        </div>

        <form
          onSubmit={handleSearch}
          className="rounded-2xl border border-gray-200 bg-white p-3 shadow-sm flex items-center gap-2"
        >
          <TextInput
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cliente, nota..."
            icon={HiSearch}
            className="flex-1"
          />
          <Button type="submit" size="sm">
            Filtrar
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => setFilterModalOpen(true)}
            className="py-1.5 bg-gray-500"
          >
            <HiFilter />
          </Button>
        </form>

        <Modal
          show={filterModalOpen}
          onClose={() => setFilterModalOpen(false)}
          size="md"
        >
          <Modal.Header>Filtros</Modal.Header>
          <Modal.Body>
            <div className="space-y-3">
              <div className="space-y-1 text-sm">
                <label className="text-[11px] uppercase tracking-[0.3em] text-gray-500">
                  Status
                </label>
                <Select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="all">Todas</option>
                  <option value="open">Abertos</option>
                  <option value="done">Finalizado</option>
                  <option value="canceled">Cancelado</option>
                </Select>
              </div>
              <div className="space-y-1 text-sm">
                <label className="text-[11px] uppercase tracking-[0.3em] text-gray-500">
                  Tipo
                </label>
                <Select value={type} onChange={(e) => setType(e.target.value)}>
                  <option value="">Todos</option>
                  <option value="ligacao">Ligação</option>
                  <option value="reuniao">Reunião</option>
                  <option value="email">E-mail</option>
                  <option value="visita">Visita</option>
                  <option value="followup">Follow-up</option>
                </Select>
              </div>
              <div className="space-y-1 text-sm">
                <label className="text-[11px] uppercase tracking-[0.3em] text-gray-500">
                  Responsável
                </label>
                <Select
                  value={responsibleId}
                  onChange={(e) => setResponsibleId(e.target.value)}
                >
                  <option value="">Todos</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              onClick={() => {
                handleSearch();
                setFilterModalOpen(false);
              }}
            >
              Aplicar
            </Button>
          </Modal.Footer>
        </Modal>
      </div>

      {/* Conteúdo principal */}
      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <SkeletonRow key={i} />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          dateBR={
            viewMode === "calendar"
              ? calendarMode === "week"
                ? `${toBRDate(weekStartISO)} a ${toBRDate(weekEndISO)}`
                : calendarMode === "month"
                ? `${toBRDate(firstMonthDay)} a ${toBRDate(lastMonthDay)}`
                : viewBR
              : viewBR
          }
        />
      ) : viewMode === "list" ? (
        // ======== LIST/TABLE VIEW ========
        <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
          <div className="px-3 py-2 border-b bg-gray-50 text-sm text-gray-600">
            {viewBR}
          </div>
          <div className="overflow-y-auto overflow-x-auto">
            <table className="min-w-[900px] table-fixed w-full">
              <thead>
                <tr className="bg-gray-100">
                  <th className="sticky top-0 z-20 bg-gray-50/95 backdrop-blur px-3 py-2 text-left text-[11px] font-semibold text-gray-600 w-40">
                    Data/Hora
                  </th>
                  <th className="sticky top-0 z-20 bg-gray-50/95 backdrop-blur px-3 py-2 text-left text-[11px] font-semibold text-gray-600 w-48">
                    Tipo
                  </th>
                  <th className="sticky top-0 z-20 bg-gray-50/95 backdrop-blur px-3 py-2 text-left text-[11px] font-semibold text-gray-600">
                    Cliente/Empresa
                  </th>
                  <th className="sticky top-0 z-20 bg-gray-50/95 backdrop-blur px-3 py-2 text-left text-[11px] font-semibold text-gray-600 w-60">
                    Responsável
                  </th>
                  <th className="sticky top-0 z-20 bg-gray-50/95 backdrop-blur px-3 py-2 text-center text-[11px] font-semibold text-gray-600 w-44">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {[...filtered]
                  .sort(
                    (a, b) =>
                      +new Date(a?.dueDate || 0) - +new Date(b?.dueDate || 0)
                  )
                  .map((t) => (
                    <ListRowTr
                      key={t.id}
                      task={t}
                      onDone={markDone}
                      onCancel={cancelTask}
                      onOpenCard={openCardFromTask}
                      overdue={isOverdueTask(t, viewDateISO)}
                    />
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : calendarMode === "day" ? (
        // ======== CALENDÁRIO — DIA ========
        <div className="rounded-2xl border border-gray-200 bg-white">
          {/* Cabeçalho */}
          <div className="grid grid-cols-[72px,1fr] sm:grid-cols-[88px,1fr] gap-3 sm:gap-4 items-center px-3 md:px-4 py-3 border-b bg-gradient-to-r from-white to-gray-50">
            <div className="sticky left-0 -ml-2 sm:-ml-3 pl-2 sm:pl-3 pr-1 sm:pr-2">
              <span className="text-[10px] sm:text-[11px] uppercase tracking-wider text-gray-400">
                Horário
              </span>
            </div>
            <div className="text-xs text-gray-500">Dia — {viewBR}</div>
          </div>

          {/* Lista rolável */}
          <div
            ref={listRef}
            className="relative px-3 md:px-4 py-2"
            style={{ maxHeight: "72vh", overflowY: "auto" }}
          >
            {showNow && (
              <div
                className="absolute left-[72px] sm:left-[88px] right-3 md:right-4 z-10"
                style={{
                  top: `calc((${
                    nowHour - hours[0]
                  } * 64px) + (${now.getMinutes()} / 60) * 64px + 8px)`,
                }}
              >
                <div className="relative">
                  <div className="absolute -left-2 w-2 h-2 rounded-full bg-rose-600 shadow" />
                  <div className="h-px bg-rose-500/70" />
                </div>
              </div>
            )}

            <div className="space-y-1">
              {hours.map((h) => (
                <div key={h} data-hour={h}>
                  <HourRow
                    hour={h}
                    isCurrent={isToday && h === nowHour}
                    tasks={(byHour.get(h) || []).sort((a, b) => {
                      const am = extractTime(a.dueDate).minute ?? 0;
                      const bm = extractTime(b.dueDate).minute ?? 0;
                      return am - bm;
                    })}
                    onDone={markDone}
                    onCancel={cancelTask}
                    onOpenCard={openCardFromTask}
                    viewDateISO={viewDateISO}
                  />
                </div>
              ))}
            </div>

            {/* Sem horário */}
            {noTime?.length ? (
              <div className="mt-4">
                <div className="flex items-center gap-2 text-gray-700 mb-2">
                  <span className="text-xs font-semibold uppercase tracking-wide">
                    Sem horário
                  </span>
                  <span className="text-xs text-gray-400">
                    ({noTime.length})
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2 sm:gap-3">
                  {noTime.map((t) => (
                    <CalendarTaskCard
                      key={t.id}
                      task={t}
                      onDone={markDone}
                      onCancel={cancelTask}
                      onOpenCard={openCardFromTask}
                      overdue={isOverdueTask(t, viewDateISO)}
                    />
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : calendarMode === "week" ? (
        // ======== CALENDÁRIO — SEMANA ========
        <div className="rounded-2xl border border-gray-200 bg-gray-100 overflow-hidden">
          <div className="px-3 md:px-4 py-3 border-b bg-gradient-to-r from-white to-gray-50 text-xs text-gray-500 bg-white">
            Semana — {toBRDate(weekStartISO)} a {toBRDate(weekEndISO)}
          </div>

          <div className="overflow-x-auto">
            {/* grade sempre com 7 colunas; largura mínima pra não quebrar no mobile */}
            <div
              className="min-w-[980px] sm:min-w-[1120px] grid grid-cols-7 gap-2 sm:gap-3 p-2 sm:p-3"
              style={{ minWidth: "860px" }}
            >
              {weekDays.map((dayISO) => {
                const list = tasksByDayWeek.get(dayISO) || [];
                const label = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"][
                  (fromISODateLocal(dayISO).getDay() + 6) % 7
                ];
                const isTodayCol = sameDay(dayISO, todayISO);

                return (
                  <div
                    key={dayISO}
                    className={[
                      "rounded-xl border bg-white flex flex-col gap-2 overflow-hidden",
                      isTodayCol ? "border-[#7A082D]" : "border-gray-200",
                    ].join(" ")}
                  >
                    <div
                      className={`flex items-center justify-between px-2 py-2 ${
                        isTodayCol ? "bg-primary" : ""
                      }`}
                    >
                      <div
                        className={`text-[11px] sm:text-xs uppercase ${
                          isTodayCol ? "text-white" : "text-gray-500"
                        }`}
                      >
                        {label}
                      </div>
                      <div
                        className={[
                          "text-[12px] sm:text-sm font-semibold",
                          isTodayCol ? "text-white" : "text-gray-800",
                        ].join(" ")}
                      >
                        {toBRDate(dayISO)}
                      </div>
                    </div>
                    <div className="p-2">
                      {list.length === 0 ? (
                        <div
                          className="text-xs text-gray-400 italic flex items-center justify-center text-center"
                          style={{ minHeight: "450px" }}
                        >
                          Sem tarefas para este dia.
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {list.map((t) => (
                            <CalendarTaskCard
                              key={t.id}
                              task={t}
                              compact
                              overdue={isOverdueTask(t, dayISO)}
                              onOpenCard={openCardFromTask}
                              onCancel={cancelTask}
                              onDone={markDone}
                              isWeek
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        // ======== CALENDÁRIO — MÊS ========
        <div className="rounded-2xl border border-gray-100 bg-white shadow-md overflow-hidden">
          <div className="px-3 md:px-4 py-3 border-b bg-gradient-to-r from-white to-gray-50 text-xs text-gray-500">
            Mês — {toBRDate(firstMonthDay)} a {toBRDate(lastMonthDay)}
          </div>

          {/* Cabeçalho dos dias */}

          <div className="overflow-x-scroll">
            <div
              className="grid grid-cols-7 text-[10px] sm:text-[11px] text-gray-500 px-2 sm:px-3 pt-3"
              style={{ minWidth: "860px" }}
            >
              {["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"].map((d) => (
                <div key={d} className="px-1 pb-2">
                  {d}
                </div>
              ))}
            </div>

            <div
              className="grid grid-cols-7 gap-1.5 sm:gap-2 p-2 sm:p-3 pt-2 bg-gray-50"
              style={{ minWidth: "860px" }}
            >
              {monthMatrix.map((week, wi) =>
                week.map((dayISO, di) => {
                  const inMonth =
                    dayISO >= firstMonthDay && dayISO <= lastMonthDay;
                  const list = tasksByDayMonth.get(dayISO) || [];
                  const isTodayCell = sameDay(dayISO, todayISO);

                  return (
                    <div
                      key={`${wi}-${di}`}
                      className={[
                        " rounded-lg border p-2 flex flex-col gap-1",
                        isTodayCell
                          ? "bg-red-50 shadow-md"
                          : inMonth
                          ? "border-gray-200 bg-white"
                          : "border-gray-300 bg-gray-50/40",
                      ].join(" ")}
                      style={{ minHeight: "150px" }}
                    >
                      <div className="flex items-center justify-between">
                        <span
                          className={[
                            "text-[11px] sm:text-xs font-semibold inline-flex items-center justify-center w-6 h-6 rounded-full",
                            isTodayCell
                              ? "bg-[#7A082D] text-white ring-2 ring-[#7A082D]/40"
                              : inMonth
                              ? "text-gray-800"
                              : "text-gray-400",
                          ].join(" ")}
                        >
                          {fromISODateLocal(dayISO).getDate()}
                        </span>
                        {!!list.length && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-200 border border-gray-200 text-gray-700">
                            {list.length}
                          </span>
                        )}
                      </div>

                      {list.length ? (
                        <div className="space-y-1 mt-1">
                          {list.slice(0, 3).map((t) => {
                            const tm = extractTime(t?.dueDate);
                            const TypeIcon = TYPE_ICON[t?.type] || HiClock;
                            return (
                              <div
                                key={t.id}
                                className="text-[10px] sm:text-[11px] rounded-lg border bg-white px-2 py-1 flex items-center gap-1 cursor-pointer hover:shadow-sm"
                                onClick={() => openCardFromTask(t)}
                                title="Ver card"
                              >
                                <TypeIcon className="w-3.5 h-3.5 text-gray-600" />
                                <span className="tabular-nums text-gray-700">
                                  {tm.hasTime ? tm.label : ""}
                                </span>
                                <span className="text-gray-400">•</span>
                                <span className="truncate text-gray-800">
                                  {t?.card?.lead?.name ||
                                    t?.card?.lead?.companyName ||
                                    "Sem informação"}
                                </span>
                              </div>
                            );
                          })}
                          {list.length > 3 && (
                            <div className="text-[10px] text-gray-500">
                              +{list.length - 3} mais
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-[11px] text-gray-400 italic flex justify-center items-center h-full -mt-4">
                          Sem tarefas para este dia
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL DO CARD/TAREFA ===== */}
      {cardModalOpen && modalCardId && (
        <TaskModal
          task={selectedTask}
          onClose={closeCardModal}
          updateTask={updateTask}
          history={modalHistory}
          setHistory={setModalHistory}
          setSelectedTask={setSelectedTask}
          stages={(modalStages || []).sort(
            (a, b) => (a.order ?? 0) - (b.order ?? 0)
          )}
          onMoveStage={onMoveStageFromModal}
          onRefetchCard={onRefetchCard}
          onCardDeleted={onCardDeleted}
          users={users}
          labels={modalLabels}
          loading={modalLoading} // se o TaskModal exibe spinner
        />
      )}
    </div>
  );
}
